/**
 * Same-Origin API Client
 * 
 * Eliminates CORS preflights by calling /api/* on the same origin,
 * which proxies to Supabase server-side via Vercel/Netlify/Cloudflare rewrites.
 * 
 * CAPABILITY-BASED DETECTION:
 * On app boot, probes /api/functions/health-check to check if proxy is configured.
 * If probe returns JSON → use same-origin proxy.
 * If probe returns HTML or fails → fallback to supabase.functions.invoke().
 * 
 * X-REQUEST-ID CORRELATION:
 * All edge function calls include a unique x-request-id header for end-to-end tracing.
 */

import { supabase } from '@/integrations/supabase/client';

const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

interface QueryOptions {
  select?: string;
  filters?: Array<{ column: string; op: string; value: unknown }>;
  order?: { column: string; ascending?: boolean };
  range?: { from: number; to: number };
  /** When true, request a single object response from PostgREST */
  single?: boolean;
  /** Ask PostgREST for counts */
  count?: 'exact' | 'planned' | 'estimated';
}

interface ApiResponse<T> {
  data: T | null;
  error: Error | null;
  count?: number | null;
}

// Capability detection state
let proxyCapabilityChecked = false;
let proxyAvailable = false;
let proxyCheckPromise: Promise<boolean> | null = null;
let modeLogged = false;

/**
 * Generate a unique request ID for correlation.
 */
function generateRequestId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Probe the /api/functions/health-check endpoint to detect if proxy is configured.
 * Returns true if proxy works (JSON response), false otherwise (HTML/error).
 */
async function probeProxyCapability(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  
  const hostname = window.location.hostname;
  const isLocalDev = hostname === 'localhost' || hostname === '127.0.0.1';
  
  // Skip probe in local dev - always use SDK
  if (isLocalDev) {
    console.log('[sameOrigin] Local dev detected, using SDK directly');
    return false;
  }
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3s timeout
    const requestId = generateRequestId();
    
    const response = await fetch('/api/functions/health-check', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'x-request-id': requestId,
      },
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    const contentType = response.headers.get('Content-Type') || '';
    
    // Check if we got HTML (SPA fallback = proxy not configured)
    if (contentType.includes('text/html')) {
      console.warn(
        '[sameOrigin] PROXY NOT CONFIGURED: /api/functions/health-check returned HTML. ' +
        'Configure /api/functions/* rewrites to Supabase edge functions. ' +
        'Falling back to direct SDK calls.'
      );
      return false;
    }
    
    // Check if we got valid JSON
    if (response.ok && contentType.includes('application/json')) {
      const data = await response.json();
      if (data.ok === true) {
        console.log('[sameOrigin] Proxy available: health-check passed, x-request-id=' + requestId);
        return true;
      }
    }
    
    console.warn('[sameOrigin] Proxy check failed: unexpected response', {
      status: response.status,
      contentType,
    });
    return false;
  } catch (error) {
    const err = error as Error;
    if (err.name === 'AbortError') {
      console.warn('[sameOrigin] Proxy check timed out, using SDK');
    } else {
      console.warn('[sameOrigin] Proxy check failed:', err.message);
    }
    return false;
  }
}

/**
 * Get proxy availability (cached after first check).
 * Uses singleton promise to prevent concurrent probes.
 */
async function getProxyAvailability(): Promise<boolean> {
  if (proxyCapabilityChecked) {
    return proxyAvailable;
  }
  
  if (!proxyCheckPromise) {
    proxyCheckPromise = probeProxyCapability().then((available) => {
      proxyAvailable = available;
      proxyCapabilityChecked = true;
      
      // Log mode decision at boot/first call
      const hostname = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
      console.log(`[sameOrigin] ====== MODE DECISION ======`);
      console.log(`[sameOrigin] mode = ${available ? 'proxy' : 'sdk'}`);
      console.log(`[sameOrigin] reason = ${available ? 'health-check passed' : 'proxy not available or misconfigured'}`);
      console.log(`[sameOrigin] hostname = ${hostname}`);
      console.log(`[sameOrigin] ===========================`);
      
      return available;
    });
  }
  
  return proxyCheckPromise;
}

/**
 * Synchronous check - returns cached value or false if not yet checked.
 * Use shouldUseSameOriginProxyAsync() for guaranteed accuracy.
 */
function shouldUseSameOriginProxy(): boolean {
  if (!proxyCapabilityChecked) {
    // Trigger async check but return false for now
    getProxyAvailability();
    return false;
  }
  return proxyAvailable;
}

/**
 * Async check - waits for probe to complete.
 */
async function shouldUseSameOriginProxyAsync(): Promise<boolean> {
  return getProxyAvailability();
}

/**
 * Build PostgREST query string from options
 */
function buildQueryString(table: string, options?: QueryOptions): string {
  const params = new URLSearchParams();
  
  // Select columns
  if (options?.select) {
    params.set('select', options.select);
  }
  
  // Filters
  if (options?.filters) {
    for (const filter of options.filters) {
      params.append(filter.column, `${filter.op}.${filter.value}`);
    }
  }
  
  // Ordering
  if (options?.order) {
    params.set('order', `${options.order.column}.${options.order.ascending ? 'asc' : 'desc'}`);
  }
  
  return `/api/rest/${table}?${params.toString()}`;
}

/**
 * Get auth headers including user token if logged in
 */
async function getHeaders(extra?: Record<string, string>): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    apikey: SUPABASE_ANON_KEY,
    'Content-Type': 'application/json',
    ...extra,
  };

  // Add auth token if user is logged in
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`;
  }

  return headers;
}

/**
 * Fallback SELECT using Supabase SDK directly
 */
async function supabaseSelect<T>(
  table: string,
  options?: QueryOptions
): Promise<ApiResponse<T[] | T>> {
  try {
    // Use type assertion to allow dynamic table names
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let query = (supabase.from as any)(table).select(
      options?.select || '*',
      options?.count ? { count: options.count } : undefined
    );

    // Apply filters
    if (options?.filters) {
      for (const filter of options.filters) {
        const { column, op, value } = filter;
        switch (op) {
          case 'eq':
            query = query.eq(column, value);
            break;
          case 'neq':
            query = query.neq(column, value);
            break;
          case 'gt':
            query = query.gt(column, value);
            break;
          case 'gte':
            query = query.gte(column, value);
            break;
          case 'lt':
            query = query.lt(column, value);
            break;
          case 'lte':
            query = query.lte(column, value);
            break;
          case 'like':
            query = query.like(column, String(value));
            break;
          case 'ilike':
            query = query.ilike(column, String(value));
            break;
          case 'is':
            query = query.is(column, value as null);
            break;
          case 'in':
            query = query.in(column, value as unknown[]);
            break;
          case 'not.is':
            query = query.not(column, 'is', value);
            break;
          default:
            query = query.eq(column, value);
        }
      }
    }

    // Apply ordering
    if (options?.order) {
      query = query.order(options.order.column, { ascending: options.order.ascending ?? true });
    }

    // Apply range for pagination
    if (options?.range) {
      query = query.range(options.range.from, options.range.to);
    }

    // Execute as single or array
    if (options?.single) {
      const { data, error, count } = await query.maybeSingle();
      if (error) {
        return { data: null, error: new Error(error.message), count: null };
      }
      return { data: data as T, error: null, count };
    }

    const { data, error, count } = await query;
    if (error) {
      return { data: null, error: new Error(error.message), count: null };
    }
    return { data: (data || []) as T[], error: null, count };
  } catch (error) {
    return { data: null, error: error as Error, count: null };
  }
}

/**
 * Same-origin SELECT query - falls back to Supabase SDK when proxy unavailable
 */
export async function sameOriginSelect<T>(
  table: string,
  options?: QueryOptions
): Promise<ApiResponse<T[] | T>> {
  // Use direct Supabase in preview/localhost environments
  if (!shouldUseSameOriginProxy()) {
    return supabaseSelect<T>(table, options);
  }

  try {
    const url = buildQueryString(table, options);

    const headers = await getHeaders(
      options?.single
        ? { Accept: 'application/vnd.pgrst.object+json' }
        : undefined
    );

    // Ask PostgREST for counts when paginating
    if (options?.count) {
      headers.Prefer = `count=${options.count}`;
    }

    // Add range headers if specified
    if (options?.range) {
      headers['Range-Unit'] = 'items';
      headers.Range = `${options.range.from}-${options.range.to}`;
      headers.Prefer = headers.Prefer ? `${headers.Prefer},count=exact` : 'count=exact';
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    // PostgREST returns 406 (PGRST116) for "0 rows" when requesting a single object.
    if (!response.ok) {
      if (options?.single && response.status === 406) {
        return { data: null, error: null, count: null };
      }
      const error = await response.json().catch(() => ({}));
      return { data: null, error: new Error(error.message || 'Request failed') };
    }

    const data = await response.json();

    // Parse count from Content-Range header if present
    let count: number | null = null;
    const contentRange = response.headers.get('Content-Range');
    if (contentRange) {
      const match = contentRange.match(/\/(\d+)/);
      if (match) {
        count = parseInt(match[1], 10);
      }
    }

    return { data, error: null, count };
  } catch (error) {
    // Fallback to Supabase SDK on network errors
    console.warn('[sameOrigin] Proxy failed, falling back to Supabase SDK:', error);
    return supabaseSelect<T>(table, options);
  }
}

/**
 * Fallback INSERT using Supabase SDK
 */
async function supabaseInsert<T>(
  table: string,
  body: unknown,
  options?: { select?: string }
): Promise<ApiResponse<T>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let query = (supabase.from as any)(table).insert(body as Record<string, unknown>);
    
    if (options?.select) {
      query = query.select(options.select);
    }

    const { data, error } = await query;
    if (error) {
      return { data: null, error: new Error(error.message) };
    }
    return { data: (Array.isArray(data) ? data?.[0] : data) as T, error: null };
  } catch (error) {
    return { data: null, error: error as Error };
  }
}

/**
 * Same-origin INSERT - falls back to Supabase SDK
 */
export async function sameOriginInsert<T>(
  table: string,
  body: unknown,
  options?: { select?: string }
): Promise<ApiResponse<T>> {
  if (!shouldUseSameOriginProxy()) {
    return supabaseInsert<T>(table, body, options);
  }

  try {
    const headers = await getHeaders();
    headers['Prefer'] = options?.select ? 'return=representation' : 'return=minimal';
    
    let url = `/api/rest/${table}`;
    if (options?.select) {
      url += `?select=${options.select}`;
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    
    if (!response.ok) {
      const error = await response.json();
      return { data: null, error: new Error(error.message || 'Insert failed') };
    }
    
    if (options?.select) {
      const data = await response.json();
      return { data, error: null };
    }
    
    return { data: null, error: null };
  } catch (error) {
    console.warn('[sameOrigin] Proxy failed, falling back to Supabase SDK:', error);
    return supabaseInsert<T>(table, body, options);
  }
}

/**
 * Fallback UPDATE using Supabase SDK
 */
async function supabaseUpdate<T>(
  table: string,
  body: unknown,
  options?: QueryOptions
): Promise<ApiResponse<T>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let query = (supabase.from as any)(table).update(body as Record<string, unknown>);

    // Apply filters
    if (options?.filters) {
      for (const filter of options.filters) {
        const { column, op, value } = filter;
        if (op === 'eq') {
          query = query.eq(column, value);
        } else if (op === 'neq') {
          query = query.neq(column, value);
        }
      }
    }

    if (options?.select) {
      query = query.select(options.select);
    }

    const { data, error } = await query;
    if (error) {
      return { data: null, error: new Error(error.message) };
    }
    return { data: (Array.isArray(data) ? data?.[0] : data) as T, error: null };
  } catch (error) {
    return { data: null, error: error as Error };
  }
}

/**
 * Same-origin UPDATE - falls back to Supabase SDK
 */
export async function sameOriginUpdate<T>(
  table: string,
  body: unknown,
  options?: QueryOptions
): Promise<ApiResponse<T>> {
  if (!shouldUseSameOriginProxy()) {
    return supabaseUpdate<T>(table, body, options);
  }

  try {
    const headers = await getHeaders();
    headers.Prefer = options?.select ? 'return=representation' : 'return=minimal';

    const params = new URLSearchParams();
    if (options?.select) params.set('select', options.select);
    if (options?.filters) {
      for (const filter of options.filters) {
        params.append(filter.column, `${filter.op}.${filter.value}`);
      }
    }

    const url = `/api/rest/${table}?${params.toString()}`;

    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      return { data: null, error: new Error(error.message || 'Update failed') };
    }

    if (options?.select) {
      const data = await response.json();
      return { data, error: null };
    }

    return { data: null, error: null };
  } catch (error) {
    console.warn('[sameOrigin] Proxy failed, falling back to Supabase SDK:', error);
    return supabaseUpdate<T>(table, body, options);
  }
}

/**
 * Fallback DELETE using Supabase SDK
 */
async function supabaseDelete(
  table: string,
  options?: Pick<QueryOptions, 'filters'>
): Promise<ApiResponse<null>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let query = (supabase.from as any)(table).delete();

    if (options?.filters) {
      for (const filter of options.filters) {
        const { column, op, value } = filter;
        if (op === 'eq') {
          query = query.eq(column, value);
        }
      }
    }

    const { error } = await query;
    if (error) {
      return { data: null, error: new Error(error.message) };
    }
    return { data: null, error: null };
  } catch (error) {
    return { data: null, error: error as Error };
  }
}

/**
 * Same-origin DELETE - falls back to Supabase SDK
 */
export async function sameOriginDelete(
  table: string,
  options?: Pick<QueryOptions, 'filters'>
): Promise<ApiResponse<null>> {
  if (!shouldUseSameOriginProxy()) {
    return supabaseDelete(table, options);
  }

  try {
    const headers = await getHeaders();
    headers.Prefer = 'return=minimal';

    const params = new URLSearchParams();
    if (options?.filters) {
      for (const filter of options.filters) {
        params.append(filter.column, `${filter.op}.${filter.value}`);
      }
    }

    const url = `/api/rest/${table}?${params.toString()}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      return { data: null, error: new Error(error.message || 'Delete failed') };
    }

    return { data: null, error: null };
  } catch (error) {
    console.warn('[sameOrigin] Proxy failed, falling back to Supabase SDK:', error);
    return supabaseDelete(table, options);
  }
}

/**
 * Fallback RPC using Supabase SDK
 */
async function supabaseRpc<T>(
  fnName: string,
  args: Record<string, unknown> = {}
): Promise<ApiResponse<T>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)(fnName, args);
    if (error) {
      return { data: null, error: new Error(error.message) };
    }
    return { data: data as T, error: null };
  } catch (error) {
    return { data: null, error: error as Error };
  }
}

/**
 * Same-origin RPC (PostgREST) - falls back to Supabase SDK
 */
export async function sameOriginRpc<T>(
  fnName: string,
  args: Record<string, unknown> = {}
): Promise<ApiResponse<T>> {
  if (!shouldUseSameOriginProxy()) {
    return supabaseRpc<T>(fnName, args);
  }

  try {
    const headers = await getHeaders();

    const response = await fetch(`/api/rest/rpc/${fnName}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(args),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      return { data: null, error: new Error(error.message || 'RPC failed') };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    console.warn('[sameOrigin] Proxy failed, falling back to Supabase SDK:', error);
    return supabaseRpc<T>(fnName, args);
  }
}

/**
 * Parse a response safely, with hardened error handling.
 * Throws a detailed error if the response is not valid JSON.
 */
async function parseJsonResponse<T>(response: Response, requestUrl: string): Promise<T> {
  const contentType = response.headers.get('Content-Type') || '';
  
  // Check if Content-Type indicates JSON
  if (!contentType.includes('application/json')) {
    const text = await response.text();
    const preview = text.slice(0, 200);
    throw new Error(
      `[sameOrigin] Invalid Content-Type: expected application/json, got "${contentType}". ` +
      `Status: ${response.status}, URL: ${requestUrl}, Body preview: ${preview}`
    );
  }
  
  try {
    return await response.json() as T;
  } catch (parseError) {
    const text = await response.clone().text().catch(() => '[unable to read body]');
    const preview = text.slice(0, 200);
    throw new Error(
      `[sameOrigin] JSON parse error. Status: ${response.status}, URL: ${requestUrl}, ` +
      `Body preview: ${preview}`
    );
  }
}

/**
 * Validate and normalize edge function name.
 * - Strips /api/functions/ prefix if present
 * - Validates format matches allowed pattern
 * - Throws descriptive error if invalid
 */
function validateFunctionName(rawName: string): string {
  let name = rawName;
  
  // Strip common prefix mistakes
  if (name.startsWith('/api/functions/')) {
    console.warn(`[sameOrigin.fn] Stripping /api/functions/ prefix from: ${rawName}`);
    name = name.replace('/api/functions/', '');
  }
  if (name.startsWith('api/functions/')) {
    console.warn(`[sameOrigin.fn] Stripping api/functions/ prefix from: ${rawName}`);
    name = name.replace('api/functions/', '');
  }
  
  // Remove any leading/trailing slashes
  name = name.replace(/^\/+|\/+$/g, '');
  
  // Validate format: alphanumeric, hyphens, underscores only
  const validPattern = /^[a-z0-9][a-z0-9_-]*[a-z0-9]$|^[a-z0-9]$/i;
  if (!validPattern.test(name)) {
    throw new Error(
      `[sameOrigin.fn] Invalid function name: "${rawName}" (normalized: "${name}"). ` +
      `Must match pattern: alphanumeric with hyphens/underscores, no slashes.`
    );
  }
  
  return name;
}

/**
 * Fallback edge function using Supabase SDK (supabase.functions.invoke)
 * Includes x-request-id for correlation.
 */
async function supabaseFunction<T>(
  functionName: string,
  options?: {
    body?: unknown;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    query?: Record<string, string>;
  }
): Promise<ApiResponse<T>> {
  const validatedName = validateFunctionName(functionName);
  const requestId = generateRequestId();

  const bodyKeys = (() => {
    const b = options?.body;
    if (!b || typeof b !== 'object') return [] as string[];
    try {
      return Object.keys(b as Record<string, unknown>);
    } catch {
      return [] as string[];
    }
  })();

  console.log('[sameOrigin.fn][request]', {
    mode: 'sdk',
    functionNameRaw: functionName,
    functionName: validatedName,
    method: options?.method || 'POST',
    bodyKeys,
    requestId,
  });

  try {
    const { data, error } = await supabase.functions.invoke(validatedName, {
      body: options?.body,
      headers: {
        'x-request-id': requestId,
      },
    });

    if (error) {
      console.error('[sameOrigin.fn][sdk-error]', {
        functionName: validatedName,
        message: error.message,
        name: error.name,
        context: error.context,
        requestId,
      });
      return {
        data: null,
        error: new Error(
          `Edge function "${validatedName}" failed via SDK. ` +
            `message=${error.message}, x-request-id=${requestId}`
        ),
      };
    }

    console.log('[sameOrigin.fn][sdk-ok]', { functionName: validatedName, requestId });
    return { data: data as T, error: null };
  } catch (e) {
    const err = e as Error;
    console.error('[sameOrigin.fn][sdk-exception]', {
      functionName: validatedName,
      message: err.message,
      name: err.name,
      requestId,
    });
    return {
      data: null,
      error: new Error(
        `Edge function "${validatedName}" SDK exception: ${err.message}, x-request-id=${requestId}`
      ),
    };
  }
}

/**
 * Same-origin edge function call - uses capability-based proxy detection
 * 
 * CAPABILITY-BASED BEHAVIOR:
 * - Probes /api/functions/_health on first call to detect proxy availability
 * - If proxy available → uses /api/functions/* same-origin proxy
 * - If proxy not available → uses supabase.functions.invoke() directly
 * 
 * HARDENED ERROR HANDLING:
 * - Validates function name format before calling
 * - Detects HTML responses (proxy misconfigured)
 * - Provides detailed error messages including status, URL, and body preview
 */
export async function sameOriginFunction<T>(
  functionName: string,
  options?: {
    body?: unknown;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    query?: Record<string, string>;
  }
): Promise<ApiResponse<T>> {
  // Validate function name first (catches errors early)
  let validatedName: string;
  try {
    validatedName = validateFunctionName(functionName);
  } catch (validationError) {
    console.error('[sameOrigin.fn][validation-error]', {
      functionName,
      message: (validationError as Error).message,
    });
    return { data: null, error: validationError as Error };
  }

  const bodyKeys = (() => {
    const b = options?.body;
    if (!b || typeof b !== 'object') return [] as string[];
    try {
      return Object.keys(b as Record<string, unknown>);
    } catch {
      return [] as string[];
    }
  })();

  // Use async capability check - waits for probe to complete
  const useProxy = await shouldUseSameOriginProxyAsync();
  
  if (!useProxy) {
    console.log('[sameOrigin.fn][route]', {
      mode: 'sdk',
      reason: 'proxy not available (capability check)',
      functionNameRaw: functionName,
      functionName: validatedName,
      method: options?.method || 'POST',
      bodyKeys,
    });
    return supabaseFunction<T>(validatedName, options);
  }

  // Production: use same-origin proxy
  const qs = new URLSearchParams(options?.query || {}).toString();
  const url = `/api/functions/${validatedName}${qs ? `?${qs}` : ''}`;

  console.log('[sameOrigin.fn][request]', {
    mode: 'proxy',
    functionNameRaw: functionName,
    functionName: validatedName,
    url,
    method: options?.method || 'POST',
    bodyKeys,
  });

  try {
    const headers = await getHeaders();

    const response = await fetch(url, {
      method: options?.method || 'POST',
      headers,
      body: options?.body ? JSON.stringify(options.body) : undefined,
    });

    const contentType = response.headers.get('Content-Type') || '';

    // CRITICAL: Enforce JSON content-type in proxy mode
    // If we get HTML, the proxy is misconfigured (returning SPA fallback)
    if (!contentType.includes('application/json')) {
      const text = await response.text().catch(() => '');
      const preview = (text || '').slice(0, 200);
      const isHtmlPage = preview.toLowerCase().includes('<!doctype') || preview.toLowerCase().includes('<html');
      
      const errCode = isHtmlPage ? 'PROXY_MISCONFIGURED' : 'INVALID_CONTENT_TYPE';
      const errMsg = isHtmlPage
        ? `[sameOrigin.fn] PROXY_MISCONFIGURED: ${url} returned HTML instead of JSON. ` +
          `Configure /api/functions/* rewrites to proxy to Supabase. ` +
          `Status: ${response.status}, Preview: ${preview.slice(0, 100)}`
        : `[sameOrigin.fn] ${errCode}: Expected application/json, got "${contentType}". ` +
          `URL: ${url}, Status: ${response.status}, Preview: ${preview.slice(0, 100)}`;

      console.error(`[sameOrigin.fn][${errCode.toLowerCase()}]`, {
        functionName: validatedName,
        url,
        status: response.status,
        contentType,
        isHtmlPage,
        preview: preview.slice(0, 100),
      });

      const error = new Error(errMsg);
      (error as Error & { code: string }).code = errCode;
      return { data: null, error };
    }

    if (!response.ok) {
      // Grab a preview for evidence (works even for HTML)
      const text = await response.text().catch(() => '');
      const preview = (text || '').slice(0, 200);

      const errMsg = `[sameOrigin.fn] HTTP ${response.status} calling ${url}. ` +
        `content-type="${contentType}" body-preview="${preview}"`;

      console.error('[sameOrigin.fn][http-error]', {
        functionName: validatedName,
        url,
        status: response.status,
        contentType,
        preview,
      });

      return { data: null, error: new Error(errMsg) };
    }

    // Handle 204 No Content
    if (response.status === 204) {
      console.log('[sameOrigin.fn][204]', { functionName: validatedName, url });
      return { data: null as unknown as T, error: null };
    }

    // Parse JSON with hardened error handling (will throw descriptive error)
    const data = await parseJsonResponse<T>(response, url);
    console.log('[sameOrigin.fn][ok]', { functionName: validatedName, url });
    return { data, error: null };
  } catch (e) {
    const err = e as Error;
    const message = `[sameOrigin.fn] Network error calling ${url}: ${err.message}`;
    console.error('[sameOrigin.fn][network-error]', { functionName: validatedName, url, message });
    return { data: null, error: new Error(message) };
  }
}

export const sameOrigin = {
  select: sameOriginSelect,
  insert: sameOriginInsert,
  update: sameOriginUpdate,
  delete: sameOriginDelete,
  rpc: sameOriginRpc,
  fn: sameOriginFunction,
};
